import { FunctionComponent, memo } from "react";
import createPersistedState from "use-persisted-state";
import { Form, Formik, FormikHelpers } from "formik";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@edgetier/components";
import { IFormValues, IProps } from "./choose-tag-form.types";
import SelectTagGroups from "components-for/filters/select-tag-groups";
import SelectTag from "components-for/filters/select-tag";
import { PersistedStorageKeys } from "constants/persisted-storage-keys";
import { useQueryParameters } from "@edgetier/client-components";
import { parseUrlArray } from "@edgetier/utilities";
import { isValueEmpty } from "utilities/is-value-empty";
import * as Yup from "yup";
import "./choose-tag-form.scss";

const useTagSelected = createPersistedState<string[]>(PersistedStorageKeys.TagSelected);
const useTagGroupSelected = createPersistedState<string[]>(PersistedStorageKeys.TagGroupSelected);

/**
 * Form to select which tag should have their values shown
 * @param props.closeAction     Callback for closing the any external containers
 */
const ChooseTagForm: FunctionComponent<IProps> = ({ closeAction, customColumnIndex }) => {
    // Retrieve any existing tags in URL
    const { tagId: queryTagId } = useQueryParameters({ tagId: parseUrlArray });

    // Read tag group and tag
    const [tagGroupSelected, setTagGroupSelected] = useTagGroupSelected([]);
    const [tagSelected, setTagSelected] = useTagSelected([]);

    const selectedIndex = typeof customColumnIndex === "number" ? customColumnIndex : 0;

    /**
     * Submit choose tag form
     * @param formValues
     */
    const tagSubmit = (formValues: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => {
        if (!tagSelected.includes(formValues.tag)) {
            const tagGroupHasValues = !isValueEmpty(formValues.tagGroup);
            // Set tagGroup.
            setTagGroupSelected((values) => {
                if (Array.isArray(values)) {
                    return [
                        ...values.slice(0, selectedIndex),
                        ...(tagGroupHasValues ? [formValues.tagGroup] : []),
                        ...values.slice(selectedIndex + 1),
                    ];
                }
                return values;
            });
            // Set tag only when tag group has a value.
            setTagSelected((values) => {
                if (Array.isArray(values)) {
                    return [
                        ...values.slice(0, selectedIndex),
                        ...(tagGroupHasValues ? [formValues.tag] : []),
                        ...values.slice(selectedIndex + 1),
                    ];
                }
                return values;
            });
        } else {
            formikHelpers.setSubmitting(false);
        }
        if (typeof closeAction === "function") {
            closeAction();
        }
    };

    return (
        <Formik<IFormValues>
            initialValues={{
                tagGroup: tagGroupSelected[selectedIndex] ?? "",
                tag: tagSelected[selectedIndex] ?? "",
            }}
            validationSchema={Yup.object({
                tagGroup: Yup.string().trim().required("Required"),
                tag: Yup.string().trim().required("Required"),
            })}
            onSubmit={tagSubmit}
        >
            {({ values, dirty, isSubmitting, isValid }) => (
                <Form autoComplete="off" name="choose-tag-form" className="choose-tag-form">
                    <div className="field">
                        <label htmlFor="tagGroup">Tag Group</label>
                        <SelectTagGroups name="tagGroup" isSingleSelect hasAddItemMenu={false} isClearable />
                    </div>
                    <div className="field">
                        <label htmlFor="tag">Tag</label>
                        <SelectTag
                            name="tag"
                            isClearable
                            isDisabled={isValueEmpty(values.tagGroup)}
                            description="tag"
                            parameters={{ tagId: queryTagId }}
                            tagGroupId={isValueEmpty(values.tagGroup) ? undefined : values.tagGroup}
                            isSingleSelect
                        />
                    </div>
                    <Button
                        type="submit"
                        icon={faCheck}
                        disabled={!dirty || !isValid || isSubmitting}
                        isLoading={isSubmitting}
                    >
                        Apply
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default memo(ChooseTagForm);
